import type { ChangeEvent, JSX } from 'react';
import { createContext, useMemo, useContext } from 'react';
import { isArray } from '@wistia/type-guards';
import type { FormGroupProps } from './FormGroup';
import { FormGroup } from './FormGroup';
import { FormContext } from '../Form/Form';

export type CheckboxGroupProps = FormGroupProps & {
  onChange?: ((event: ChangeEvent<HTMLInputElement>) => void) | undefined;
  value?: string[] | undefined;
  name: string;
};

type CheckboxGroupContextValues = Pick<CheckboxGroupProps, 'name' | 'onChange' | 'value'>;

export const CheckboxGroupContext = createContext<CheckboxGroupContextValues | null>(null);

/**
 * Connects Checkbox components with their parent CheckboxGroup; provides access to `name` and `onChange`
 */
export const useCheckboxGroup = () => {
  return useContext(CheckboxGroupContext);
};

/**
 * For grouping like form elements together. Can serve as a checkbox
 */
export const CheckboxGroup = ({
  children,
  name,
  onChange,
  value,
  ...props
}: CheckboxGroupProps): JSX.Element => {
  const formState = useContext(FormContext);
  const derivedValue = isArray(formState.values[name])
    ? (formState.values[name] as string[])
    : value;
  const context = useMemo<CheckboxGroupContextValues>(() => {
    return {
      name: `${name}[]`,
      onChange,
      value: derivedValue,
    };
  }, [name, derivedValue, onChange]);

  return (
    <CheckboxGroupContext.Provider value={context}>
      <FormGroup {...props}>{children}</FormGroup>
    </CheckboxGroupContext.Provider>
  );
};

CheckboxGroup.displayName = 'CheckboxGroup';
